import { Platform } from 'react-native';
import { API_URL } from '../config/api';
import { Resource, CreateResourceRequest, UploadFileResponse } from '../types/resource';
import authService from './authService';

class ResourceService {
  private baseUrl = `${API_URL}/resources`;

  private async getHeaders(): Promise<HeadersInit> {
    const token = await authService.getAuthToken();
    
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    
    return {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    };
  }

  private async getFormDataHeaders(): Promise<HeadersInit> {
    const token = await authService.getAuthToken();
    
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }
    
    return {
      'x-auth-token': token,
    };
  }

  // Get all resources for the authenticated user
  async getMyResources(): Promise<Resource[]> {
    try {
      const response = await fetch(`${this.baseUrl}/my`, {
        method: 'GET',
        headers: await this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching my resources:', error);
      throw error;
    }
  }

  // Get saved resources
  async getSavedResources(): Promise<Resource[]> {
    try {
      const response = await fetch(`${this.baseUrl}/saved`, {
        method: 'GET',
        headers: await this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching saved resources:', error);
      throw error;
    }
  }

  // Create a new resource
  async createResource(resourceData: CreateResourceRequest): Promise<Resource> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify(resourceData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating resource:', error);
      throw error;
    }
  }

  // Upload a file
  async uploadFile(file: any): Promise<UploadFileResponse> {
    try {
      const formData = new FormData();
      
      // For web, we need to create a proper File object
      if (Platform.OS === 'web') {
        // Convert the file URI to a File object for web
        const response = await fetch(file.uri);
        const blob = await response.blob();
        const fileObj = new File([blob], file.name, { type: file.type });
        formData.append('file', fileObj);
      } else {
        // For mobile, use the standard React Native format
        formData.append('file', {
          uri: file.uri,
          name: file.name,
          type: file.type,
        } as any);
      }

      const response = await fetch(`${this.baseUrl}/upload`, {
        method: 'POST',
        headers: await this.getFormDataHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  // Helper method to get the resource ID
  private getResourceId(resource: Resource): string {
    return resource.id || resource._id || '';
  }

  // Save a resource (toggles save state)
  async saveResource(resourceId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${resourceId}/save`, {
        method: 'POST',
        headers: await this.getHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Save error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving resource:', error);
      throw error;
    }
  }

  // Unsave a resource (same as save - toggles state)
  async unsaveResource(resourceId: string): Promise<void> {
    // The backend save endpoint toggles, so we can use the same endpoint
    return this.saveResource(resourceId);
  }

  // Delete a resource
  async deleteResource(resourceId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${resourceId}`, {
        method: 'DELETE',
        headers: await this.getHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  }

  // Get resource URL for opening
  getResourceUrl(resource: Resource): string {
    if (resource.url?.startsWith('http')) {
      return resource.url;
    }
    return `${API_URL}${resource.url}`;
  }

  // Get authenticated resource URL for mobile viewing
  async getAuthenticatedResourceUrl(resource: Resource): Promise<string> {
    try {
      const token = await authService.getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }
      
      // For documents, we need to create a temporary authenticated URL
      if (resource.type === 'document') {
        // Create a blob URL with authentication
        const response = await fetch(this.getResourceUrl(resource), {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }
        
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      } else {
        // For links and videos, return the original URL
        return this.getResourceUrl(resource);
      }
    } catch (error) {
      console.error('Error getting authenticated URL:', error);
      throw error;
    }
  }
}

export default new ResourceService();


